import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  Alert,
  CircularProgress,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import CircularLoader from "../../../common/components/fundamentals/Loader/CircularLoader";
import CustomPopUp from "../../../common/components/utils/CustomPopUp";
import featureFlagsService from "../../../api/FeatureFlagsService";
import { FeatureFlag } from "../../../common/models/Models";
import { HeadingText, SmallText } from "../../../common/styles/Text.styles";
import {
  borderStyle,
  smallerGap,
  smallGap,
  tinyGap,
} from "../../../styles/consts";
import {
  InternalSettingsAreaWithPadding,
  SettingsArea,
} from "../Settings.styles";

const QUERY_KEY = ["featureFlags"];

const LABEL_BY_NAME: Record<string, string> = {
  supervisor_enabled: "Supervisor approval",
  auto_send_enabled: "Auto-send application on CV confirm",
};

type PendingToggle = { name: string; nextEnabled: boolean };

const confirmationCopy = (
  flagName: string,
  nextEnabled: boolean
): { heading: string; content: string } => {
  if (flagName === "supervisor_enabled") {
    return nextEnabled
      ? {
          heading: "Enable supervisor approval?",
          content:
            "Consultant-confirmed CVs will require supervisor approval before reaching the sales team.",
        }
      : {
          heading: "Disable supervisor approval?",
          content:
            "Consultant-confirmed CVs will be sent directly to the sales team, skipping supervisor review.",
        };
  }
  if (flagName === "auto_send_enabled") {
    return nextEnabled
      ? {
          heading: "Enable auto-send?",
          content:
            "When a consultant confirms a CV and a recruiter email is available, the application is emailed automatically, skipping sales review. Email-sourced offers still go through sales.",
        }
      : {
          heading: "Disable auto-send?",
          content:
            "Consultant-confirmed CVs will go to sales/supervisor for manual sending instead of being emailed automatically.",
        };
  }
  return {
    heading: nextEnabled ? "Enable this setting?" : "Disable this setting?",
    content: "This change will take effect within 30 seconds.",
  };
};

export default function FeatureFlagsPanel() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [pendingToggle, setPendingToggle] = useState<PendingToggle | null>(null);

  const { data: flags, isLoading } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => featureFlagsService.listFlags(),
  });

  const mutation = useMutation({
    mutationFn: ({ name, enabled }: { name: string; enabled: boolean }) =>
      featureFlagsService.setFlag(name, enabled),
    onMutate: async ({ name, enabled }) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEY });
      const previous = queryClient.getQueryData<FeatureFlag[]>(QUERY_KEY);
      queryClient.setQueryData<FeatureFlag[]>(QUERY_KEY, (old) =>
        (old ?? []).map((f) => (f.name === name ? { ...f, enabled } : f))
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(QUERY_KEY, context.previous);
      setError("Failed to update flag. Please try again.");
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });

  return (
    <SettingsArea sx={{ maxHeight: "28rem" }}>
      <Stack sx={{ borderBottom: borderStyle }}>
        <Stack sx={{ padding: smallerGap }}>
          <HeadingText>Feature Flags</HeadingText>
          <SmallText>
            Operational toggles. Changes take effect within 30 seconds.
          </SmallText>
        </Stack>
      </Stack>

      <InternalSettingsAreaWithPadding sx={{ height: "100%", gap: smallerGap }}>
        {isLoading ? (
          <Stack sx={{ alignItems: "center" }}>
            <CircularLoader size={"2rem"} />
          </Stack>
        ) : (
          (flags ?? []).map((flag) => {
            const title = LABEL_BY_NAME[flag.name] ?? flag.name;
            const formattedDate = new Date(flag.updated_at).toLocaleDateString();
            const isThisPending =
              mutation.isPending && mutation.variables?.name === flag.name;
            return (
              <Paper
                key={flag.name}
                elevation={0}
                sx={{
                  padding: `${tinyGap} ${smallGap}`,
                  border: borderStyle,
                  borderRadius: 0,
                  backgroundColor: "#fafafa",
                }}
              >
                <Stack sx={{ gap: "0.1rem" }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: "1rem",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {title}
                    </Typography>
                    <Switch
                      size="small"
                      checked={flag.enabled}
                      disabled={mutation.isPending}
                      onChange={(_, checked) =>
                        setPendingToggle({ name: flag.name, nextEnabled: checked })
                      }
                    />
                  </Stack>
                  {flag.description && (
                    <Typography sx={{ fontSize: "0.75rem", lineHeight: 1.35 }}>
                      {flag.description}
                    </Typography>
                  )}
                  {isThisPending ? (
                    <Stack
                      direction="row"
                      alignItems="center"
                      sx={{ gap: "0.4rem" }}
                    >
                      <CircularProgress size={12} sx={{ color: "#ff5950" }} />
                      <Typography
                        sx={{ fontSize: "0.7rem", color: "#ff5950", lineHeight: 1.35 }}
                      >
                        Saving...
                      </Typography>
                    </Stack>
                  ) : (
                    <Typography
                      sx={{ fontSize: "0.7rem", color: "#ff5950", lineHeight: 1.35 }}
                    >
                      Changed by {flag.updated_by} on {formattedDate}
                    </Typography>
                  )}
                </Stack>
              </Paper>
            );
          })
        )}
      </InternalSettingsAreaWithPadding>

      <Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>

      {pendingToggle && (() => {
        const copy = confirmationCopy(pendingToggle.name, pendingToggle.nextEnabled);
        return (
          <CustomPopUp
            isPopUpOpen={true}
            setPopUpOpen={(open) => {
              if (!open) setPendingToggle(null);
            }}
            heading={copy.heading}
            content={copy.content}
            button2={pendingToggle.nextEnabled ? "Enable" : "Disable"}
            action={() => {
              mutation.mutate({
                name: pendingToggle.name,
                enabled: pendingToggle.nextEnabled,
              });
              setPendingToggle(null);
            }}
          />
        );
      })()}
    </SettingsArea>
  );
}
